import { useEffect, useRef, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiError } from "../lib/api";
import { useCart } from "../contexts/CartContext";
import { useAuth } from "../contexts/AuthContext";
import { useSettings } from "../contexts/SettingsContext";
import { toast } from "sonner";
import { Lock, ShieldCheck, Smartphone, Plus, Pencil, Star, MapPin } from "lucide-react";
import AddressModal, { EMPTY_ADDRESS, validateAddress as validateUnifiedAddress } from "../components/AddressModal";
import CartItemImage from "../components/CartItemImage";
import { trackInitiateCheckout, trackAddPaymentInfo, trackPurchase } from "../lib/metaPixel";
import { pushBeginCheckout, pushAddShippingInfo, pushAddPaymentInfo as gtmAddPaymentInfo, pushPurchase } from "../lib/gtmDataLayer";
import SEOHead from "../components/SEOHead";

// UPI apps that Razorpay's intent flow supports on mobile.
// Brand SVG sources from Razorpay's CDN (public, used in their own checkout).
const UPI_APPS = [
  { id: "google_pay", name: "GPay", icon: "https://cdn.razorpay.com/static/assets/upi_intent_apps/google_pay.png" },
  { id: "phonepe",    name: "PhonePe", icon: "https://cdn.razorpay.com/static/assets/upi_intent_apps/phonepe.png" },
  { id: "paytm",      name: "Paytm",   icon: "https://cdn.razorpay.com/static/assets/upi_intent_apps/paytm.png" },
  { id: "cred",       name: "CRED",    icon: "https://cdn.razorpay.com/static/assets/upi_intent_apps/cred.png" },
];

const isMobile = () => typeof navigator !== "undefined" && /Mobi|Android|iPhone|iPad/i.test(navigator.userAgent);

export default function Checkout() {
  const { items, subtotal, clear } = useCart();
  const { user, loading } = useAuth();
  const { freeDeliveryEnabled, handlingFee, activeCharges, chargesTotal, settings: storeSettings, codEnabled } = useSettings();
  const navigate = useNavigate();
  const [submitting, setSubmitting] = useState(false);
  // While true, an opaque overlay covers the Checkout page so the customer
  // never sees the checkout flash back between Razorpay closing and the
  // /order/:id page mounting after a successful payment.
  const [verifyingPayment, setVerifyingPayment] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState("razorpay");

  // iter96 / Session C — if admin has disabled COD, force the selection
  // back to razorpay.  Handles the edge case of a stale checkout tab
  // that was opened while COD was ON and the customer had selected it.
  useEffect(() => {
    if (!codEnabled && paymentMethod === "cod") {
      setPaymentMethod("razorpay");
    }
  }, [codEnabled, paymentMethod]);
  const placedRef = useRef(false);
  const [savedAddresses, setSavedAddresses] = useState([]);
  const [selectedAddrId, setSelectedAddrId] = useState(null);
  const [addrModal, setAddrModal] = useState({ open: false, mode: "add", data: EMPTY_ADDRESS, saving: false });

  // The single source of truth for which address ships this order.
  const selectedAddr = savedAddresses.find((a) => a.address_id === selectedAddrId) || null;

  // Load saved addresses on mount (after user is known) and auto-select default.
  useEffect(() => {
    if (!user) return;
    api.get("/auth/addresses").then((r) => {
      const list = r.data || [];
      setSavedAddresses(list);
      if (list.length > 0) {
        const def = list.find((a) => a.is_default) || list[0];
        setSelectedAddrId(def.address_id);
      }
    }).catch(() => {});
  }, [user]);

  useEffect(() => {
    if (loading) return;
    if (placedRef.current) return;
    if (items.length === 0) { navigate("/cart"); return; }
  }, [loading, items.length, navigate]);

  // iter52 — fire Meta InitiateCheckout once per Checkout mount with a
  // populated cart. We guard against multiple fires within the same mount
  // (re-renders, address selection refresh) via a ref.
  const initiateCheckoutFiredRef = useRef(false);
  useEffect(() => {
    if (loading) return;
    if (items.length === 0) return;
    if (initiateCheckoutFiredRef.current) return;
    initiateCheckoutFiredRef.current = true;
    trackInitiateCheckout(items, { subtotal, total: subtotal });
    pushBeginCheckout(items, { value: subtotal });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, items.length]);

  // iter57 — fire GA4 add_shipping_info whenever an address is selected.
  // Ref-guarded by selectedAddr.address_id so picking the same address twice
  // does not duplicate-fire.
  const lastShippingAddrIdRef = useRef(null);
  useEffect(() => {
    if (loading) return;
    if (items.length === 0) return;
    const addrId = selectedAddr?.address_id || selectedAddr?.pincode;
    if (!addrId) return;
    if (lastShippingAddrIdRef.current === addrId) return;
    lastShippingAddrIdRef.current = addrId;
    pushAddShippingInfo(items, { value: subtotal, shippingTier: "Standard" });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, items.length, selectedAddr]);

  useEffect(() => {
    if (document.getElementById("razorpay-script")) return;
    const s = document.createElement("script");
    s.id = "razorpay-script";
    s.src = "https://checkout.razorpay.com/v1/checkout.js";
    document.body.appendChild(s);
  }, []);

  // ── Dynamic Shiprocket shipping quote ──────────────────────────────────
  // Recalculate the courier rate whenever the selected address pincode or
  // cart contents change.  iter77: NO fallback.  If Shiprocket returns 400
  // we surface the error inline and BLOCK "Proceed to Payment".
  const [quote, setQuote] = useState(null);
  const [quoteLoading, setQuoteLoading] = useState(false);
  const [quoteError, setQuoteError] = useState("");

  const selectedPincode = selectedAddr?.pincode || "";

  useEffect(() => {
    const pin = (selectedPincode || "").trim();
    if (!/^\d{6}$/.test(pin) || items.length === 0) {
      setQuote(null);
      setQuoteError("");
      return;
    }
    let cancelled = false;
    setQuoteLoading(true);
    setQuoteError("");
    api.post("/shipping/quote", {
      items: items.map((i) => ({ product_id: i.product_id, quantity: i.quantity, variant_label: i.variant_label })),
      pincode: pin,
      cod: paymentMethod === "cod",
    })
      .then((r) => { if (!cancelled) { setQuote(r.data); setQuoteError(""); } })
      .catch((err) => {
        if (cancelled) return;
        setQuote(null);
        setQuoteError(
          err?.response?.data?.detail ||
          "We could not calculate delivery for this pincode. Please verify the address and try again."
        );
      })
      .finally(() => { if (!cancelled) setQuoteLoading(false); });
    return () => { cancelled = true; };
    // `items` (full reference) is intentionally NOT included — quantity-only
    // changes already re-trigger via `subtotal` and item-count changes via
    // `items.length`. Adding `items` would cause a refetch on every cart
    // mutation including in-place reference updates.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedPincode, subtotal, items.length, paymentMethod]);

  const delivery = quote ? Number(quote.rate || 0) : 0;
  // Only show "Free" when admin has explicitly enabled the free-delivery feature.
  const isFreeDelivery = freeDeliveryEnabled && !!quote?.free;
  // iter69 — Grand Total = Product Total + Shipping Charge + all active Charges.
  // Session L / iter109 — dynamic charges take precedence over the
  // legacy flat handling fee when the admin has configured any.
  const total = subtotal + delivery + (chargesTotal || handlingFee);
  // iter77 — payment is blocked until we have a live quote with no error.
  const canPay = !!quote && !quoteError && !quoteLoading && !!selectedAddr;

  /**
   * iter61 — Fire the Meta + GA4 Purchase event EXACTLY ONCE per order.
   * Called synchronously from Checkout at the moment the order is
   * committed (COD insert response, Razorpay verify response). The
   * `purchase_pushed_<order_id>` sessionStorage flag guards against a
   * second fire from OrderConfirmation's fallback effect. Wrapped in a
   * try/catch so analytics can NEVER break the checkout success flow.
   */
  const firePurchaseOnce = (order) => {
    try {
      if (!order || !order.order_id) return;
      const k = `purchase_pushed_${order.order_id}`;
      if (sessionStorage.getItem(k) === "1") return;
      sessionStorage.setItem(k, "1");
      trackPurchase(order);
      pushPurchase(order);
    } catch (e) {
      // Best-effort analytics — never block checkout.
    }
  };

  const openAddressModal = (mode = "add", source = null) => {
    if (mode === "edit" && source) {
      setAddrModal({ open: true, mode: "edit", data: { ...source }, saving: false });
    } else {
      // pre-fill name/phone from user when adding the first address
      setAddrModal({
        open: true,
        mode: "add",
        data: {
          ...EMPTY_ADDRESS,
          full_name: user?.name || "",
          phone: user?.phone || "",
          // first-ever address will be auto-promoted server-side, but pre-tick if none exist
          is_default: savedAddresses.length === 0,
        },
        saving: false,
      });
    }
  };
  const closeAddrModal = () => setAddrModal({ open: false, mode: "add", data: EMPTY_ADDRESS, saving: false });

  const saveAddrFromModal = async () => {
    const err = validateUnifiedAddress(addrModal.data);
    if (err) { toast.error(err); return; }
    setAddrModal((m) => ({ ...m, saving: true }));

    // Guest path: keep the address in local state only — no /auth/addresses POST.
    if (!user) {
      const guestId = `guest_${Date.now()}`;
      const guestAddr = { ...addrModal.data, address_id: guestId, is_default: true };
      setSavedAddresses([guestAddr]);
      setSelectedAddrId(guestId);
      toast.success("Delivery address saved for this order");
      closeAddrModal();
      return;
    }

    try {
      let list;
      if (addrModal.mode === "add") {
        const r = await api.post("/auth/addresses", addrModal.data);
        list = r.data;
        toast.success("Address saved to your account");
      } else {
        const r = await api.put(`/auth/addresses/${addrModal.data.address_id}`, addrModal.data);
        list = r.data;
        toast.success("Address updated");
      }
      setSavedAddresses(list);
      const targetId = addrModal.mode === "add"
        ? (list[list.length - 1] || list[0])?.address_id
        : addrModal.data.address_id;
      const selected = list.find((x) => x.address_id === targetId) || list[0];
      if (selected) setSelectedAddrId(selected.address_id);
      closeAddrModal();
    } catch (e) {
      toast.error(formatApiError(e));
      setAddrModal((m) => ({ ...m, saving: false }));
    }
  };

  /**
   * Build Razorpay options.
   *  - upiApp: optional UPI app id ("phonepe","google_pay","paytm","cred") to preselect
   *           the UPI intent flow for that app. Only applies on mobile.
   *  - upiOnly: if true, restrict checkout to UPI only (used by the quick-action UPI buttons)
   */
  const buildRazorpayOptions = (intent, { upiApp = null, upiOnly = false } = {}) => {
    const opts = {
      key: intent.key_id || storeSettings.razorpay_key_id,
      amount: intent.amount,
      currency: intent.currency || "INR",
      name: "Sri Durga Foods",
      description: `Sri Durga Foods order`,
      image: "/favicon.ico",
      order_id: intent.razorpay_order_id,
      prefill: {
        name: selectedAddr?.full_name || user?.name || "",
        email: user?.email || "",
        contact: selectedAddr?.phone || user?.phone || "",
      },
      notes: { intent_id: intent.intent_id },
      theme: { color: "#E11D24" },
      retry: { enabled: true, max_count: 2 },
      method: {
        upi: true,
        card: true,
        netbanking: true,
        wallet: true,
        paylater: true,
        emi: true,
      },
      modal: { escape: true, backdropclose: false, confirm_close: true },
    };

    if (upiApp && isMobile()) {
      opts.prefill.method = "upi";
      opts._ = { flow: "intent", upi_app: upiApp };
    }

    if (upiOnly) {
      opts.method = { upi: true, card: false, netbanking: false, wallet: false, paylater: false, emi: false };
      opts.prefill.method = "upi";
    }

    return opts;
  };

  const launchRazorpay = (intent, options) => {
    return new Promise((resolve) => {
      const rz = new window.Razorpay({
        ...options,
        handler: async (response) => {
          // Cover the checkout page IMMEDIATELY so the customer never sees
          // it again after Razorpay closes — even if /verify-payment takes
          // a second or two on slow networks.
          setVerifyingPayment(true);
          placedRef.current = true;
          try {
            // iter51: verify-payment now consumes the intent_id and inserts
            // the order on the server. We need the returned order_id for
            // navigation.
            const { data: verified } = await api.post("/orders/verify-payment", {
              intent_id: intent.intent_id,
              razorpay_order_id: response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature,
            });
            // iter61 — fire Meta + GA4 Purchase IMMEDIATELY here. Same
            // rationale as the COD branch — guarantees the push happens
            // exactly once at the moment of confirmation, decoupled from
            // /order/<id> page hydration which can be skipped on hard
            // refreshes after a successful order.
            firePurchaseOnce(verified);
            toast.success("Payment successful — thank you!");
            // replace:true so the browser Back button doesn't return to checkout.
            navigate(`/order/${verified.order_id}`, { replace: true });
            clear();
            resolve("success");
          } catch (err) {
            // Verification failed — the intent has either been signature-
            // rejected (and auto-deleted server-side) or already consumed.
            // No order was created on our side, so we route the user back
            // to the cart with a clear message instead of an order page.
            toast.error("Payment verification failed. Please retry or contact support.");
            setVerifyingPayment(false);
            setSubmitting(false);
            navigate("/cart", { replace: true });
            resolve("verify_failed");
          }
        },
        modal: {
          ...options.modal,
          ondismiss: () => {
            // iter51: nothing was saved, so just drop the intent so it
            // doesn't linger in the DB until the TTL expires.
            api.post("/payments/razorpay/discard-intent", { intent_id: intent.intent_id }).catch(() => {});
            // iter101 — COD is no longer unconditionally offered.  Show a
            // neutral retry-only message when the customer closes the
            // Razorpay modal without paying.
            toast("Payment Failed. You can Retry Again.");
            setSubmitting(false);
            resolve("cancelled");
          },
        },
      });
      rz.on("payment.failed", function (response) {
        const err = response?.error || {};
        api.post("/orders/payment-failed", {
          intent_id: intent.intent_id,
          reason: err.description || "Payment failed",
          razorpay_payment_id: err.metadata?.payment_id || "",
          error_code: err.code || "",
          error_description: err.description || "",
        }).catch(() => {});
        toast.error(`Payment failed: ${err.description || "Please try again."}`);
        setSubmitting(false);
        resolve("failed");
      });
      rz.open();
    });
  };

  // Strip transient fields (address_id, is_default, created_at) before sending to /orders.
  const addressForOrder = () => {
    if (!selectedAddr) return null;
    const { address_id, is_default, created_at, ...payload } = selectedAddr;
    return payload;
  };

  // iter51: Razorpay path now requests a *payment intent* — the actual
  // order document is only inserted server-side AFTER signature
  // verification. This eliminates orphan / duplicate / abandoned orders.
  const createPaymentIntent = async () => {
    const { data: intent } = await api.post("/payments/razorpay/create-intent", {
      items: items.map((i) => ({ product_id: i.product_id, quantity: i.quantity, variant_label: i.variant_label })),
      address: addressForOrder(),
      customer_email: user?.email || undefined,
    });
    return intent;
  };

  const handlePay = async (e) => {
    e.preventDefault();
    const minOrder = Number(storeSettings?.min_order_amount || 200);
    if (subtotal < minOrder) {
      toast.error(`MINIMUM ORDERING AMOUNT IS ₹${minOrder.toFixed(0)}`);
      navigate("/cart");
      return;
    }
    if (!selectedAddr) {
      toast.error("Please add a delivery address first");
      return;
    }
    const err = validateUnifiedAddress(selectedAddr);
    if (err) { toast.error(err); return; }
    // iter77 — refuse payment until Shiprocket has confirmed the pincode
    // is serviceable and quoted a live rate.
    if (quoteLoading) {
      toast.error("Please wait — calculating shipping…");
      return;
    }
    if (quoteError || !quote) {
      toast.error(quoteError || "Please enter a serviceable delivery pincode before paying.");
      return;
    }
    setSubmitting(true);

    if (paymentMethod === "cod") {
      try {
        trackAddPaymentInfo(items, { subtotal, total }, "cod");
        gtmAddPaymentInfo(items, { value: total, paymentType: "cod" });
        const { data: order } = await api.post("/orders", {
          items: items.map((i) => ({ product_id: i.product_id, quantity: i.quantity, variant_label: i.variant_label })),
          address: addressForOrder(),
          payment_method: "cod",
        });
        placedRef.current = true;
        setVerifyingPayment(true);
        // iter61 — fire Meta + GA4 Purchase IMMEDIATELY here, while
        // Checkout is still mounted and dataLayer+GTM are guaranteed alive.
        // OrderConfirmation has a fallback push but it's gated by an
        // async fetch that can fail on a hard refresh, leaving dataLayer
        // empty. Firing here makes the purchase event 100% reliable
        // regardless of what happens on the confirmation page.
        firePurchaseOnce(order);
        toast.success("Order placed — pay on delivery");
        navigate(`/order/${order.order_id}`, { replace: true });
        clear();
      } catch (e2) {
        toast.error(formatApiError(e2));
        setSubmitting(false);
      }
      return;
    }

    // Razorpay — intent-based, no order persisted until paid.
    if (!window.Razorpay) {
      toast.error("Payment gateway is still loading. Please wait a moment and retry.");
      setSubmitting(false);
      return;
    }
    let intent;
    try { intent = await createPaymentIntent(); }
    catch (e2) { toast.error(formatApiError(e2)); setSubmitting(false); return; }
    if (!intent.razorpay_order_id) {
      toast.error("Could not create payment session. Please try again or contact support.");
      setSubmitting(false);
      return;
    }
    // iter52 — Meta AddPaymentInfo once the gateway intent is ready.
    trackAddPaymentInfo(items, { subtotal, total }, "razorpay");
    gtmAddPaymentInfo(items, { value: total, paymentType: "razorpay" });
    await launchRazorpay(intent, buildRazorpayOptions(intent));
  };

  const payViaUpiApp = async (upiApp) => {
    if (!selectedAddr) {
      toast.error("Please add a delivery address first");
      return;
    }
    const err = validateUnifiedAddress(selectedAddr);
    if (err) { toast.error(err); return; }
    if (quoteLoading) { toast.error("Please wait — calculating shipping…"); return; }
    if (quoteError || !quote) {
      toast.error(quoteError || "Please enter a serviceable delivery pincode before paying.");
      return;
    }
    if (!window.Razorpay) { toast.error("Payment gateway is still loading…"); return; }
    setSubmitting(true);
    let intent;
    try { intent = await createPaymentIntent(); }
    catch (e2) { toast.error(formatApiError(e2)); setSubmitting(false); return; }
    if (!intent.razorpay_order_id) {
      toast.error("Could not create payment session.");
      setSubmitting(false);
      return;
    }
    trackAddPaymentInfo(items, { subtotal, total }, `upi_${upiApp}`);
    gtmAddPaymentInfo(items, { value: total, paymentType: `upi_${upiApp}` });
    await launchRazorpay(intent, buildRazorpayOptions(intent, { upiApp, upiOnly: true }));
  };

  return (
    <div className="container-x py-12 lg:py-20 animate-fade-in" data-testid="checkout-page">
      <SEOHead
        title="Secure Checkout | Sri Durga Foods"
        description={codEnabled
          ? "Complete your Sri Durga Foods order — pay via UPI, cards, netbanking or Cash on Delivery. Free delivery on eligible orders."
          : "Complete your Sri Durga Foods order — pay securely via UPI, cards or netbanking. Free delivery on eligible orders."}
        noindex
      />
      {verifyingPayment && (
        <div
          className="fixed inset-0 z-[300] bg-brand-cream flex flex-col items-center justify-center text-center px-6"
          data-testid="payment-verifying-overlay"
          role="status"
          aria-live="polite"
        >
          <div className="w-12 h-12 rounded-full border-2 border-brand-chili/20 border-t-brand-chili animate-spin mb-6" />
          <div className="font-serif text-2xl text-brand-dark mb-1">Confirming your order</div>
          <div className="text-sm text-brand-muted">Please don&apos;t refresh — we&apos;re finalising the payment.</div>
        </div>
      )}
      <div className="mb-10">
        <span className="overline mb-3 block">Final Step</span>
        <h1 className="font-serif text-4xl sm:text-5xl text-brand-dark">Checkout</h1>
      </div>

      <form onSubmit={handlePay} className="grid grid-cols-1 lg:grid-cols-12 gap-12">
        <div className="lg:col-span-7 space-y-8">
          <div data-testid="delivery-address-block">
            <div className="flex items-center justify-between flex-wrap gap-3 mb-5">
              <h2 className="font-serif text-2xl text-brand-dark">Delivery address</h2>
              {savedAddresses.length > 0 && (
                <button
                  type="button"
                  onClick={() => openAddressModal("add")}
                  className="text-sm text-brand-chili font-medium inline-flex items-center gap-1.5 active:opacity-70 transition-opacity"
                  data-testid="checkout-add-address-btn"
                >
                  <Plus className="w-4 h-4" strokeWidth={2} /> Add new
                </button>
              )}
            </div>

            {savedAddresses.length === 0 ? (
              <div
                className="bg-white border border-dashed border-brand-line rounded-2xl p-8 text-center"
                data-testid="no-saved-addresses"
              >
                <MapPin className="w-6 h-6 mx-auto text-brand-muted mb-3" strokeWidth={1.5} />
                <p className="text-brand-muted text-sm mb-4">Add a delivery address to continue.</p>
                <button
                  type="button"
                  onClick={() => openAddressModal("add")}
                  className="btn-primary"
                  data-testid="checkout-add-first-address-btn"
                >
                  <Plus className="w-4 h-4 mr-2" strokeWidth={2} /> Add new address
                </button>
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid="checkout-address-list">
                {savedAddresses.map((a) => {
                  const isSelected = selectedAddrId === a.address_id;
                  return (
                    <button
                      type="button"
                      key={a.address_id}
                      onClick={() => setSelectedAddrId(a.address_id)}
                      className={`text-left bg-white rounded-2xl p-5 pb-3 border transition-all flex flex-col ${
                        isSelected
                          ? "border-brand-chili ring-1 ring-brand-chili/30"
                          : "border-brand-line active:border-brand-chili/40 [@media(hover:hover)]:hover:border-brand-chili/40"
                      }`}
                      data-testid={`checkout-addr-card-${a.address_id}`}
                      aria-pressed={isSelected}
                    >
                      {/* Top row: radio + name (truncates) ........ DEFAULT badge */}
                      <div className="flex items-start justify-between gap-3 mb-1">
                        <div className="flex items-center gap-2 min-w-0 flex-1">
                          <span
                            className={`w-4 h-4 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${
                              isSelected ? "border-brand-chili" : "border-brand-line"
                            }`}
                            aria-hidden="true"
                          >
                            {isSelected && <span className="w-2 h-2 rounded-full bg-brand-chili" />}
                          </span>
                          <span className="font-serif text-lg text-brand-dark truncate">{a.full_name}</span>
                        </div>
                        {a.is_default && (
                          <span
                            className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider px-2 py-0.5 bg-brand-chili text-white rounded-full flex-shrink-0 whitespace-nowrap"
                            data-testid={`checkout-default-badge-${a.address_id}`}
                          >
                            <Star className="w-2.5 h-2.5 fill-current" strokeWidth={0} /> Default
                          </span>
                        )}
                      </div>

                      <div className="text-xs text-brand-muted mb-2 pl-6">{a.phone}</div>
                      <address className="not-italic text-sm text-brand-dark/90 leading-relaxed pl-6">
                        {a.house_no}{a.street ? `, ${a.street}` : ""}{a.landmark ? ` · ${a.landmark}` : ""}<br />
                        {a.city}, {a.state} · <span className="font-medium">PIN {a.pincode}</span>
                      </address>

                      {/* Footer row: Edit lives in flow at bottom-right, never overlaps the badge */}
                      <div className="mt-3 pt-3 pl-6 border-t border-brand-line/60 flex justify-end">
                        <span
                          role="button"
                          tabIndex={0}
                          onClick={(e) => { e.stopPropagation(); openAddressModal("edit", a); }}
                          onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.stopPropagation(); openAddressModal("edit", a); } }}
                          className="inline-flex items-center gap-1.5 text-xs text-brand-muted active:text-brand-chili [@media(hover:hover)]:hover:text-brand-chili px-2 py-1 rounded-full cursor-pointer transition-colors"
                          data-testid={`checkout-edit-${a.address_id}`}
                        >
                          <Pencil className="w-3 h-3" strokeWidth={1.8} /> Edit
                        </span>
                      </div>
                    </button>
                  );
                })}
              </div>
            )}

            {selectedAddrId && savedAddresses.length > 0 && (
              <p className="text-xs text-brand-muted mt-3 flex items-center gap-1.5" data-testid="using-this-address">
                <ShieldCheck className="w-3.5 h-3.5 text-brand-success" strokeWidth={1.7} />
                Using this address for delivery.
              </p>
            )}
          </div>

          <div>
            <h2 className="font-serif text-2xl text-brand-dark mb-5">Payment</h2>

            {/* UPI Quick-action row */}
            <div className="mb-5" data-testid="upi-quick-row">
              <div className="flex items-center gap-2 mb-3">
                <Smartphone className="w-4 h-4 text-brand-chili" strokeWidth={1.5} />
                <span className="text-xs font-bold uppercase tracking-[0.15em] text-brand-muted">Pay with UPI App</span>
              </div>
              <div className="grid grid-cols-4 gap-2 sm:gap-3">
                {UPI_APPS.map((app) => (
                  <button
                    key={app.id}
                    type="button"
                    onClick={() => payViaUpiApp(app.id)}
                    disabled={submitting}
                    className="flex flex-col items-center gap-1.5 bg-white border border-brand-line rounded-xl p-3 hover:border-brand-chili hover:-translate-y-0.5 transition-all disabled:opacity-50"
                    data-testid={`upi-app-${app.id}`}
                  >
                    <img src={app.icon} alt={app.name} className="w-8 h-8 object-contain" onError={(e) => { e.currentTarget.style.display = "none"; }} />
                    <span className="text-[11px] font-medium">{app.name}</span>
                  </button>
                ))}
              </div>
            </div>

            <div className="space-y-3">
              <label className={`flex items-center gap-3 border rounded-xl p-4 cursor-pointer transition-all ${paymentMethod === "razorpay" ? "border-brand-chili bg-brand-subtle" : "border-brand-line"}`} data-testid="pay-razorpay">
                <input type="radio" name="pm" value="razorpay" checked={paymentMethod === "razorpay"} onChange={() => setPaymentMethod("razorpay")} className="accent-brand-chili" />
                <div className="flex-1">
                  <div className="font-medium text-sm">Cards · UPI · Netbanking · Wallets · Pay Later</div>
                  <div className="text-xs text-brand-muted mt-0.5">Secure Razorpay checkout</div>
                </div>
                <Lock className="w-4 h-4 text-brand-muted" strokeWidth={1.5} />
              </label>
              <label className={`flex items-center gap-3 border rounded-xl p-4 cursor-pointer transition-all ${paymentMethod === "cod" ? "border-brand-chili bg-brand-subtle" : "border-brand-line"} ${codEnabled ? "" : "hidden"}`} data-testid="pay-cod">
                <input type="radio" name="pm" value="cod" checked={paymentMethod === "cod"} onChange={() => setPaymentMethod("cod")} className="accent-brand-chili" />
                <div>
                  <div className="font-medium text-sm">Cash on Delivery</div>
                  <div className="text-xs text-brand-muted mt-0.5">Pay when your order arrives</div>
                </div>
              </label>
            </div>
          </div>
        </div>

        <div className="lg:col-span-5">
          <div className="bg-white border border-brand-line rounded-2xl p-6 sticky top-28">
            <h3 className="font-serif text-2xl text-brand-dark mb-5">Your order</h3>
            <div className="space-y-3 max-h-64 overflow-auto pr-1 mb-4">
              {items.map((it) => (
                <div key={`${it.product_id}__${it.variant_label}`} className="flex gap-3 text-sm">
                  <CartItemImage
                    src={it.image_url}
                    alt={it.name}
                    className="w-14 h-14 sm:w-16 sm:h-16 rounded-lg object-cover bg-brand-cream flex-shrink-0"
                    testid={`checkout-item-image-${it.product_id}`}
                  />
                  <div className="flex-1">
                    <div className="font-medium text-brand-dark">{it.name}</div>
                    <div className="text-xs text-brand-muted">{it.variant_label ? `${it.variant_label} · ` : ""}Qty {it.quantity} · ₹{it.price}</div>
                  </div>
                  <div className="text-sm font-medium">₹{(it.price * it.quantity).toFixed(0)}</div>
                </div>
              ))}
            </div>
            <div className="space-y-2 text-sm pt-4 border-t border-brand-line">
              <div className="flex justify-between"><span className="text-brand-muted">Product Total</span><span data-testid="checkout-subtotal-amount">₹{subtotal.toFixed(0)}</span></div>
              <div className="flex justify-between">
                <span className="text-brand-muted">Shipping Charge</span>
                <span data-testid="checkout-delivery-amount">
                  {quoteLoading
                    ? "…"
                    : isFreeDelivery
                    ? "Free"
                    : quote
                    ? `₹${Math.round(delivery)}`
                    : "—"}
                </span>
              </div>
              {/* Session L / iter109 — dynamic Charges list, matches
                   what Cart renders and what the backend server-side
                   total uses (single source of truth). */}
              {activeCharges && activeCharges.length > 0 ? (
                activeCharges.map((c) => (
                  <div key={c.id} className="flex justify-between" data-testid={`checkout-charge-${c.id}`}>
                    <span className="text-brand-muted">{c.name}</span>
                    <span>₹{Number(c.amount).toFixed(0)}</span>
                  </div>
                ))
              ) : (
                <div className="flex justify-between">
                  <span className="text-brand-muted">Handling Fee</span>
                  <span data-testid="checkout-handling-amount">₹{Number(handlingFee).toFixed(0)}</span>
                </div>
              )}
              {quote && !quote.free && (quote.courier_name || quote.etd) && (
                <div className="text-[11px] text-brand-muted -mt-1" data-testid="checkout-courier-info">
                  {quote.courier_name || "Courier"}{quote.etd ? ` · ETA ${quote.etd}` : ""}
                </div>
              )}
              {quoteError && (
                <div
                  className="mt-2 text-xs text-brand-chili bg-brand-chili/5 border border-brand-chili/20 rounded-lg px-3 py-2"
                  data-testid="checkout-shipping-error"
                  role="alert"
                >
                  {quoteError}
                </div>
              )}
              {!quote && !quoteError && !quoteLoading && (
                <div
                  className="mt-2 text-[11px] text-brand-muted bg-brand-subtle rounded-lg px-3 py-2"
                  data-testid="checkout-shipping-hint"
                >
                  Select a delivery address to view live shipping cost.
                </div>
              )}
            </div>
            <div className="border-t border-brand-line mt-4 pt-4 flex justify-between items-baseline">
              <span className="font-serif text-xl">Grand Total</span>
              <span className="font-serif text-3xl text-brand-chili font-semibold" data-testid="checkout-grand-total">₹{total.toFixed(0)}</span>
            </div>
            <button
              type="submit"
              disabled={submitting || !canPay}
              className="btn-primary w-full mt-6 disabled:opacity-60 disabled:cursor-not-allowed"
              data-testid="place-order-btn"
            >
              {submitting
                ? "Processing…"
                : quoteLoading
                ? "Calculating shipping…"
                : !selectedAddr
                ? "Add a delivery address"
                : quoteError
                ? "Fix delivery address to continue"
                : paymentMethod === "razorpay"
                ? `Pay ₹${total.toFixed(0)}`
                : `Place order · ₹${total.toFixed(0)}`}
            </button>
            <p className="text-[11px] text-brand-muted text-center mt-3">By placing the order you agree to our terms.</p>
          </div>
        </div>
      </form>

      {addrModal.open && (
        <AddressModal
          mode={addrModal.mode}
          data={addrModal.data}
          saving={addrModal.saving}
          onChange={(d) => setAddrModal((m) => ({ ...m, data: d }))}
          onClose={closeAddrModal}
          onSave={saveAddrFromModal}
        />
      )}
    </div>
  );
}
